import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { calculateRobustRisk } from '../lib/RiskEngine';
import { AUDIT_QUESTIONS } from '../data/questions';
import { ArrowRight, ArrowLeft, ShieldAlert } from 'lucide-react';

const AuditAssessment = () => {
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [config, setConfig] = useState(null);

  useEffect(() => {
    const savedConfig = JSON.parse(localStorage.getItem('aifine_config'));
    if (!savedConfig) {
      navigate('/start-audit');
    } else {
      setConfig(savedConfig);
    }
  }, [navigate]);

  const handleAnswer = (answer) => {
    const currentQuestion = AUDIT_QUESTIONS[currentIndex];
    const updatedAnswers = { ...answers, [currentQuestion.id]: answer };
    setAnswers(updatedAnswers);

    if (currentIndex < AUDIT_QUESTIONS.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      const results = calculateRobustRisk(updatedAnswers, config);
      localStorage.setItem('aifine_results', JSON.stringify(results));
      navigate('/dashboard');
    }
  };

  if (!config) return null;

  const progress = ((currentIndex + 1) / AUDIT_QUESTIONS.length) * 100;
  const currentQuestion = AUDIT_QUESTIONS[currentIndex];

  return (
    <div className="flex h-screen bg-slate-50 font-inter text-blue-900 overflow-hidden">
      <Sidebar />
      <main className="flex-1 flex flex-col items-center justify-center p-12 relative">
        
        {/* PROGRESS HUD */}
        <div className="absolute top-12 w-full max-w-4xl px-12">
          <div className="flex justify-between items-end mb-4 font-black italic tracking-tighter">
            <span className="text-xs uppercase text-blue-600">Control {currentIndex + 1} / {AUDIT_QUESTIONS.length}</span>
            <span className="text-3xl text-slate-200">{Math.round(progress)}%</span>
          </div>
          <div className="h-2 w-full bg-slate-200 rounded-full overflow-hidden shadow-inner">
            <div 
              className="h-full bg-blue-600 transition-all duration-500 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* ASSESSMENT CARD */}
        <div className="max-w-4xl w-full bg-white p-20 rounded-[4rem] shadow-2xl shadow-blue-900/10 text-center border border-slate-100 relative">
          
          <div className="absolute top-8 right-12">
            <div className="flex items-center gap-2 px-4 py-2 bg-slate-50 rounded-full border border-slate-200">
              <ShieldAlert size={14} className="text-blue-600" />
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">GRAVITY: {currentQuestion.gravity}/10</span>
            </div>
          </div>

          <div className="mb-12 text-left sm:text-center">
            <p className="text-[10px] font-black uppercase tracking-[0.4em] text-blue-600 mb-4 italic">
              {currentQuestion.category || "General Compliance"}
            </p>
            <h2 className="text-3xl md:text-5xl font-black uppercase italic tracking-tighter leading-[1.1] text-blue-950">
              "{currentQuestion.text}"
            </h2>
          </div>

          {/* RESPONSE GRID */}
          <div className="grid grid-cols-2 gap-8 max-w-2xl mx-auto mt-16">
            <button
              onClick={() => handleAnswer(true)}
              className="group py-10 rounded-[2.5rem] bg-blue-600 text-white font-black uppercase italic tracking-widest text-xl hover:bg-black transition-all shadow-2xl shadow-blue-200 active:scale-95 flex items-center justify-center gap-3"
            >
              YES <ArrowRight size={24} className="group-hover:translate-x-2 transition-transform" />
            </button>
            
            <button
              onClick={() => handleAnswer(false)}
              className="py-10 rounded-[2.5rem] bg-white border-4 border-slate-900 text-slate-900 font-black uppercase italic tracking-widest text-xl hover:bg-red-600 hover:text-white hover:border-red-600 transition-all shadow-xl active:scale-95"
            >
              NO
            </button>
          </div>

          {/* NAV CONTROLS */}
          {currentIndex > 0 && (
            <button 
              onClick={() => setCurrentIndex(currentIndex - 1)}
              className="mt-12 text-xs font-black uppercase tracking-widest text-slate-300 hover:text-blue-600 flex items-center justify-center gap-2 mx-auto transition-all"
            >
              <ArrowLeft size={16} /> Previous Control
            </button>
          )}
        </div>
      </main>
    </div>
  );
};

export default AuditAssessment;